import type { BuilderWeatherLocation, BuilderWeatherProvider } from '../schemas/builder';
import type { BuilderWeatherSnapshot } from './weather';

export interface BuilderWeatherRequest {
  provider: BuilderWeatherProvider;
  location: BuilderWeatherLocation;
}

export type BuilderWeatherTransport = (
  request: BuilderWeatherRequest, signal: AbortSignal,
) => Promise<BuilderWeatherSnapshot | null>;

export const WEATHER_REFRESH_MS = 15 * 60_000;
export const WEATHER_MAX_AGE_MS = 60 * 60_000;
const WEATHER_REQUEST_TIMEOUT_MS = 15_000;
const WEATHER_CLOCK_CHECK_MS = 60_000;
const WEATHER_MAX_INACTIVE_ENTRIES = 32;
type Listener = (snapshot: BuilderWeatherSnapshot | undefined) => void;

interface WeatherEntry {
  request: BuilderWeatherRequest;
  listeners: Set<Listener>;
  snapshot?: BuilderWeatherSnapshot;
  published?: BuilderWeatherSnapshot;
  fetchedAtMs?: number;
  lastAttemptAtMs?: number;
  inFlight?: { controller: AbortController; startedAtMs: number };
  timer?: ReturnType<typeof setTimeout>;
}

/** Units, locale and the editable place name do not change the provider response. */
export function builderWeatherRequestKey(request: BuilderWeatherRequest): string {
  return `${request.provider.type}:${request.location.latitude}:${request.location.longitude}`;
}

function freshSnapshot(entry: WeatherEntry, now: number): BuilderWeatherSnapshot | undefined {
  if (!entry.snapshot || entry.fetchedAtMs === undefined) return undefined;
  const age = now - Math.min(entry.fetchedAtMs, entry.snapshot.observedAtMs);
  return age >= 0 && age < WEATHER_MAX_AGE_MS ? entry.snapshot : undefined;
}

/** Shared cache with active-consumer polling, cancellable requests and bounded inactive entries. */
export class BuilderWeatherCache {
  private readonly entries = new Map<string, WeatherEntry>();

  public constructor(private readonly transport: BuilderWeatherTransport) {}

  public subscribe(request: BuilderWeatherRequest, listener: Listener): () => void {
    const key = builderWeatherRequestKey(request);
    let entry = this.entries.get(key);
    if (!entry) {
      entry = { request, listeners: new Set() };
      this.entries.set(key, entry);
    }
    // Reinsert when used so pruning discards the least recently used inactive location.
    this.entries.delete(key);
    this.entries.set(key, entry);
    entry.listeners.add(listener);
    listener(freshSnapshot(entry, Date.now()));
    this.tick(entry);
    this.pruneInactive();
    return () => {
      entry.listeners.delete(listener);
      if (entry.listeners.size) return;
      this.stop(entry);
      this.pruneInactive();
    };
  }

  public destroy(): void {
    for (const entry of this.entries.values()) this.stop(entry);
    this.entries.clear();
  }

  private tick(entry: WeatherEntry): void {
    if (entry.timer !== undefined) clearTimeout(entry.timer);
    entry.timer = undefined;
    if (!entry.listeners.size) return;
    const now = Date.now();
    this.publish(entry, now);
    if (entry.inFlight && (now < entry.inFlight.startedAtMs
      || now - entry.inFlight.startedAtMs >= WEATHER_REQUEST_TIMEOUT_MS)) {
      const current = entry.inFlight;
      entry.inFlight = undefined;
      current.controller.abort();
    }
    if (!entry.inFlight && (entry.lastAttemptAtMs === undefined
      || now < entry.lastAttemptAtMs || now - entry.lastAttemptAtMs >= WEATHER_REFRESH_MS)) {
      this.fetch(entry, now);
    }
    this.schedule(entry, now);
  }

  private fetch(entry: WeatherEntry, now: number): void {
    const pending = { controller: new AbortController(), startedAtMs: now };
    entry.lastAttemptAtMs = now;
    entry.inFlight = pending;
    // The microtask also turns a synchronous transport failure into a handled rejection.
    void Promise.resolve().then(() => pending.controller.signal.aborted ? null
      : this.transport(entry.request, pending.controller.signal)).then(snapshot => {
      if (entry.inFlight !== pending) return;
      const fetchedAtMs = Date.now();
      if (snapshot && Number.isFinite(snapshot.observedAtMs)
        && snapshot.observedAtMs <= fetchedAtMs + WEATHER_REFRESH_MS
        && fetchedAtMs - snapshot.observedAtMs < WEATHER_MAX_AGE_MS) {
        entry.snapshot = snapshot;
        entry.fetchedAtMs = fetchedAtMs;
      }
    }).catch(() => {
      // A failed refresh retains the previous value until its maximum age.
    }).finally(() => {
      if (entry.inFlight !== pending) return;
      entry.inFlight = undefined;
      this.publish(entry, Date.now());
      this.schedule(entry, Date.now());
    });
  }

  private publish(entry: WeatherEntry, now: number): void {
    const snapshot = freshSnapshot(entry, now);
    if (entry.published === snapshot) return;
    entry.published = snapshot;
    for (const listener of entry.listeners) listener(snapshot);
  }

  private schedule(entry: WeatherEntry, now: number): void {
    if (entry.timer !== undefined) clearTimeout(entry.timer);
    if (!entry.listeners.size) return;
    const delays = [WEATHER_CLOCK_CHECK_MS];
    if (entry.lastAttemptAtMs !== undefined) delays.push(entry.lastAttemptAtMs + WEATHER_REFRESH_MS - now);
    if (entry.inFlight) delays.push(entry.inFlight.startedAtMs + WEATHER_REQUEST_TIMEOUT_MS - now);
    if (entry.snapshot && entry.fetchedAtMs !== undefined) {
      const untilExpiry = Math.min(entry.fetchedAtMs, entry.snapshot.observedAtMs) + WEATHER_MAX_AGE_MS - now;
      if (untilExpiry > 0) delays.push(untilExpiry);
    }
    const delay = Math.max(1, Math.min(...delays.filter(value => value > 0)));
    entry.timer = setTimeout(() => this.tick(entry), delay);
  }

  private stop(entry: WeatherEntry): void {
    if (entry.timer !== undefined) clearTimeout(entry.timer);
    entry.timer = undefined;
    if (entry.inFlight) {
      const pending = entry.inFlight;
      entry.inFlight = undefined;
      entry.lastAttemptAtMs = entry.fetchedAtMs;
      pending.controller.abort();
    }
  }

  private pruneInactive(): void {
    const inactive = [...this.entries].filter(([, entry]) => !entry.listeners.size);
    for (const [key] of inactive.slice(0, Math.max(0, inactive.length - WEATHER_MAX_INACTIVE_ENTRIES))) {
      this.entries.delete(key);
    }
  }
}
